import { UniButton } from '@/design-system/components/buttons';
import { ButtonVariant } from '@/design-system/components/buttons/useButton.hook';
import { ModalHeader } from '@/design-system/components/modal';
import { TextBody, TextCaption } from '@/design-system/components/typography';
import { Pressable, ScrollView, SxProp, View } from 'dripsy';
import _ from 'lodash';
import React, { useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { UniInput, UniInputAmount } from '@/design-system/components/inputs';
import { LayoutContext } from 'contexts/LayoutContext';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { CreateCardAccountStackParamList, CreateCardAccountStackRoutes } from './type';
import { DatePicker } from '@/design-system/components/datePicker/DatePicker';
import moment from 'moment-timezone';
import { UseInputRegularVariant } from '@/design-system/components/inputs/useInput.hook';

export interface CreateCardAccountProps {
  onClose: () => void;
  onLayout: (event: any) => void;
  data: any;
  onData: (data: any) => void;
  func?: (data: any, onSuccess?: (data: any) => void, onError?: (error: any) => void) => void;
  onReturnData?: (data: any) => void;
  refresh?: () => void;
  onChangeView?: () => void;
  onBackData: (data: any) => void;
}

export const CreateCardAccountWebIndividualScreen: React.FC<CreateCardAccountProps> = (props) => {
  const { onClose, onLayout, data, onData, func, onReturnData, refresh, onChangeView, onBackData } = props;
  const { t } = useTranslation('card-account');
  const layout = useContext(LayoutContext);
  const { body, textMb, btnView, btn, textTitle, mobileView, mobileCodeView, mobileNumberView, inputSx, account } = sxStyles;

  const navigation = useNavigation<NativeStackNavigationProp<CreateCardAccountStackParamList>>();
  const navigationAny = useNavigation<any>();

  const [viewShow, setViewShow] = useState(false);
  const onChangeViewLocal = () => {
    setViewShow(!viewShow);
  };

  const [cardAccount, setCardAccount] = useState<any>(data);
  const [errorMsg, setErrorMsg] = useState<any>({
    dateOfBirth: undefined,
    firstName: undefined,
    lastName: undefined,
    email: undefined,
    annualIncome: undefined,
    occupation: undefined,
    position: undefined
  });

  const validateForm = async (data?: { [key: string]: string | undefined }) => {
    const keys = data ? Object.keys(data) : [];
    const keysLength = keys.length;
    let validateFlag = true;
    let errorMsgFlag = { ...errorMsg };
    let emailReg = /^[^,]+@[\w-]+(\.[a-zA-Z]{2,}){1,}$/;
    let dateReg = /^[1-9]\d{3}-(0[1-9]|1[0-2])-(0[1-9]|[1-2][0-9]|3[0-1])$/;

    if ((!keysLength && !cardAccount?.individual?.dateOfBirth) || (keys.includes('dateOfBirth') && !data?.dateOfBirth)) {
      errorMsgFlag.dateOfBirth = t('card-account::credit-card-new-account.glossary.field-required');
      validateFlag = false;
    } else if ((!keysLength && cardAccount?.individual?.dateOfBirth) || (keys.includes('dateOfBirth') && data?.dateOfBirth)) {
      if (!dateReg.test(!keysLength ? cardAccount?.individual?.dateOfBirth : data?.dateOfBirth)) {
        errorMsgFlag.dateOfBirth = t('card-account::credit-card-new-account.glossary.date-format-error');
        validateFlag = false;
      } else {
        errorMsgFlag.dateOfBirth = undefined;
      }
    }

    if ((!keysLength && !cardAccount?.individual?.firstName) || (keys.includes('firstName') && !data?.firstName)) {
      errorMsgFlag.firstName = t('card-account::credit-card-new-account.glossary.field-required');
      validateFlag = false;
    } else if ((!keysLength && cardAccount?.individual?.firstName) || (keys.includes('firstName') && data?.firstName)) {
      errorMsgFlag.firstName = undefined;
    }

    if ((!keysLength && !cardAccount?.individual?.lastName) || (keys.includes('lastName') && !data?.lastName)) {
      errorMsgFlag.lastName = t('card-account::credit-card-new-account.glossary.field-required');
      validateFlag = false;
    } else if ((!keysLength && cardAccount?.individual?.lastName) || (keys.includes('lastName') && data?.lastName)) {
      errorMsgFlag.lastName = undefined;
    }

    if ((!keysLength && !cardAccount?.individual?.email) || (keys.includes('email') && !data?.email)) {
      errorMsgFlag.email = t('card-account::credit-card-new-account.glossary.field-required');
      validateFlag = false;
    } else if ((!keysLength && cardAccount?.individual?.email) || (keys.includes('email') && data?.email)) {
      if (!emailReg.test(!keysLength ? cardAccount?.individual?.email : data?.email)) {
        errorMsgFlag.email = t('card-account::credit-card-new-account.glossary.email-format-error');
        validateFlag = false;
      } else {
        errorMsgFlag.email = undefined;
      }
    }

    if ((!keysLength && !cardAccount?.individual?.annualIncome) || (keys.includes('annualIncome') && !data?.annualIncome)) {
      errorMsgFlag.annualIncome = t('card-account::credit-card-new-account.glossary.field-required');
      validateFlag = false;
    } else if ((!keysLength && cardAccount?.individual?.annualIncome) || (keys.includes('annualIncome') && data?.annualIncome)) {
      errorMsgFlag.annualIncome = undefined;
    }

    if ((!keysLength && !cardAccount?.individual?.occupation) || (keys.includes('occupation') && !data?.occupation)) {
      errorMsgFlag.occupation = t('card-account::credit-card-new-account.glossary.field-required');
      validateFlag = false;
    } else if ((!keysLength && cardAccount?.individual?.occupation) || (keys.includes('occupation') && data?.occupation)) {
      errorMsgFlag.occupation = undefined;
    }

    if ((!keysLength && !cardAccount?.individual?.position) || (keys.includes('position') && !data?.position)) {
      errorMsgFlag.position = t('card-account::credit-card-new-account.glossary.field-required');
      validateFlag = false;
    } else if ((!keysLength && cardAccount?.individual?.position) || (keys.includes('position') && data?.position)) {
      errorMsgFlag.position = undefined;
    }

    setErrorMsg(errorMsgFlag);
    return validateFlag;
  };

  const changeCreateCardAccount = async () => {
    const validate = await validateForm();
    if (validate) {
      onData({ data: cardAccount });
      navigation.navigate(CreateCardAccountStackRoutes.Document, {
        onLayout,
        data: cardAccount,
        onData,
        func,
        onReturnData,
        refresh,
        onChangeView: onChangeViewLocal
      });
    }
  };

  return (
    <View
      sx={{
        height: 'auto',
        width: layout.dimensions.width,
        maxWidth: Math.min(500, layout.dimensions.width),
        maxHeight: layout.dimensions.height,
        backgroundColor: '$backgroundPrimary'
      }}
      onLayout={(event) => {
        onLayout(event);
      }}>
      <ModalHeader
        title={t('card-account::credit-card-new-account.glossary.new-account')}
        closeModal={() => navigation.getParent()?.goBack()}
      />
      <ScrollView sx={{ flex: 1, backgroundColor: 'transparent' }} nestedScrollEnabled>
        <View sx={body}>
          <TextBody sx={textMb} semiBold>
            {t('card-account::credit-card-new-account.new-account-page.text--fill-out-to-create-account')}
          </TextBody>
          <View sx={{ gap: '$5' }}>
            <UniInput
              regular={UseInputRegularVariant.date}
              value={cardAccount?.individual?.dateOfBirth}
              labelText={t('card-account::credit-card-new-account.glossary.date-of-birth')}
              errorText={errorMsg.dateOfBirth}
              onValueChange={(value) => {
                setCardAccount({ ...cardAccount, individual: { ...cardAccount?.individual, dateOfBirth: value } });
                validateForm({ dateOfBirth: value });
              }}
              placeholder={'yyyy-mm-dd'}
            />
            <View sx={{ gap: '$5', display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
              <UniInput
                value={cardAccount?.individual?.firstName}
                labelText={t('card-account::credit-card-new-account.glossary.first-name')}
                errorText={errorMsg.firstName}
                onValueChange={(value) => {
                  setCardAccount({
                    ...cardAccount,
                    individual: { ...cardAccount?.individual, firstName: value }
                  });
                  validateForm({ firstName: value });
                }}
              />
              <UniInput
                value={cardAccount?.individual?.lastName}
                labelText={t('card-account::credit-card-new-account.glossary.last-name')}
                errorText={errorMsg.lastName}
                onValueChange={(value) => {
                  setCardAccount({
                    ...cardAccount,
                    individual: { ...cardAccount?.individual, lastName: value }
                  });
                  validateForm({ lastName: value });
                }}
              />
            </View>
            <UniInput
              value={cardAccount?.individual?.email}
              labelText={t('card-account::credit-card-new-account.glossary.email')}
              errorText={errorMsg.email}
              onValueChange={(value) => {
                setCardAccount({
                  ...cardAccount,
                  individual: { ...cardAccount?.individual, email: value }
                });
                validateForm({ email: value });
              }}
              wrapperSx={errorMsg.email ? { pb: 10 } : undefined}
            />
            <UniInput
              value={cardAccount?.individual?.address}
              labelText={t('card-account::credit-card-new-account.glossary.address')}
              onValueChange={(value) => {
                setCardAccount({
                  ...cardAccount,
                  individual: { ...cardAccount?.individual, address: value }
                });
              }}
            />
            <UniInputAmount
              labelText={t('card-account::credit-card-new-account.glossary.annual-income')}
              assetPrefix={false}
              maxFlag={false}
              sxWrap={{ alignItems: 'center' }}
              asset={data?.currency}
              errorText={errorMsg.annualIncome}
              value={cardAccount?.individual?.annualIncome || 0}
              onValueChange={(value) => {
                setCardAccount({
                  ...cardAccount,
                  individual: { ...cardAccount?.individual, annualIncome: value }
                });
                validateForm({ annualIncome: value?.toString() });
              }}
            />
            <UniInput
              value={cardAccount?.individual?.occupation}
              labelText={t('card-account::credit-card-new-account.glossary.occupation')}
              errorText={errorMsg.occupation}
              onValueChange={(value) => {
                setCardAccount({
                  ...cardAccount,
                  individual: { ...cardAccount?.individual, occupation: value }
                });
                validateForm({ occupation: value });
              }}
            />
            <UniInput
              value={cardAccount?.individual?.position}
              labelText={t('card-account::credit-card-new-account.glossary.position')}
              errorText={errorMsg.position}
              onValueChange={(value) => {
                setCardAccount({
                  ...cardAccount,
                  individual: { ...cardAccount?.individual, position: value }
                });
                validateForm({ position: value });
              }}
            />
          </View>
          {viewShow && <View sx={{ height: '$1', width: '100%' }} />}
          <View sx={btnView}>
            <UniButton
              sx={btn}
              variant={ButtonVariant.outline1}
              label={t('card-account-management::set-card-limit.button--back')}
              onPress={() => {
                navigation?.goBack();
                onChangeView && onChangeView();
                onBackData(cardAccount);
              }}
            />
            <UniButton
              sx={btn}
              labelSx={{ color: '$balck' }}
              label={t('card-account::credit-card-new-account.glossary.continue')}
              onPress={changeCreateCardAccount}
            />
          </View>
        </View>
      </ScrollView>
    </View>
  );
};

const sxStyles: Record<string, SxProp> = {
  body: {
    minHeight: 200,
    minWidth: 500,
    px: 28,
    py: 28
  },
  textMb: {
    marginBottom: 20
  },
  btnView: {
    flex: 1,
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '$3',
    marginTop: 40,
    zIndex: -1
  },
  btn: {
    width: 216,
    height: 44
  },
  textTitle: {
    marginBottom: '$1'
  },
  mobileView: {
    flexDirection: 'row'
  },
  mobileCodeView: {
    width: '20%',
    mr: '$2'
  },
  inputSx: {
    width: '100%'
  },
  mobileNumberView: {
    width: '80%'
  },
  account: {
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'row',
    py: '$5',
    px: '$4',
    borderWidth: 1,
    borderColor: '#DCDCDC',
    borderRadius: 5
  }
};
